/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { Component } from 'react';
import { shape, bool, object, string, func } from 'prop-types';
import { connect } from 'react-redux';
import Truncate from 'react-truncate';

import p from 'polyglot/polyglot';

import { showSignupModal as showSignupModalAction } from 'actions/signup';

import Button from 'components/Button/Button';

import styles from './profile-info.scss';

/**
 * This is the ProfileInfo component.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 */
export class ProfileInfo extends Component {
  /**
   * Declare expected prop types.
   *
   * @type {object}
   */
  static propTypes = {
    auth: shape({
      isAuthenticated: bool.isRequired,
    }).isRequired,
    role_data: object.isRequired,
    showSignupModal: func.isRequired,
    talent_presentation: string,
  };

  static defaultProps = {
    talent_presentation: '',
  };

  state = {
    expanded: false,
    truncated: false,
  };

  handleTruncate = truncated => {
    if (this.state.truncated !== truncated) {
      this.setState({
        truncated,
      });
    }
  };

  toggleLines = event => {
    event.preventDefault();

    this.setState({
      expanded: !this.state.expanded,
    });
  };

  renderWebsiteUrl = roleData => {
    const {
      auth: { isAuthenticated },
      showSignupModal,
    } = this.props;

    const { talent_website_url: url } = roleData;

    if (!isAuthenticated) {
      return (
        <Button onClick={showSignupModal} dark wide small>
          {p.t('profile.view.website')}
        </Button>
      );
    }

    if (url) {
      return (
        <Button path={url} dark wide small>
          {p.t('profile.view.website')}
        </Button>
      );
    }

    return null;
  };

  renderAgencyUrl = profile => {
    const { agency_name: name, agency_url: url } = profile;

    if (url && name) {
      return (
        <a href={url} target="_blank" rel="noopener noreferrer">
          {name}
        </a>
      );
    }

    return null;
  };

  renderPresentation = () => {
    const { talent_presentation } = this.props;
    const { expanded, truncated } = this.state;
    const lines = 3;
    const text = talent_presentation.split('\n').map((text, i, arr) => {
      const line = <span key={i}>{text}</span>;

      if (i === arr.length - 1) {
        return line;
      }

      return [line, <br key={`br-${i}`} />];
    });

    return (
      <>
        <Truncate
          lines={!expanded && lines}
          ellipsis={
            <span className={styles.readMore}>
              ...{' '}
              <a name="" onClick={this.toggleLines}>
                {p.t('profile.view.read_more')}
              </a>
            </span>
          }
          onTruncate={this.handleTruncate}
        >
          {text}
        </Truncate>

        {!truncated && expanded && (
          <span className={styles.readMore}>
            <a name="" onClick={this.toggleLines}>
              {p.t('profile.view.read_less')}
            </a>
          </span>
        )}
      </>
    );
  };

  /**
   * Render ProfileInfo.
   *
   * @return {Object}
   */
  render() {
    const { role_data } = this.props;

    return (
      <div className={styles.wrapper}>
        <h2 className={styles.title}>{p.t('profile.view.bio')}</h2>
        <div className={styles.bio}>{this.renderPresentation()}</div>

        {role_data.agency_name && (
          <div className={styles.agency}>
            <h4 className={styles.agencyTitle}>{p.t('profile.view.agency')}</h4>
            {this.renderAgencyUrl(role_data)}
          </div>
        )}

        <div className={styles.website}>{this.renderWebsiteUrl(role_data)}</div>
      </div>
    );
  }
}

export default connect(
  ({ auth }) => ({ auth }),
  {
    showSignupModal: showSignupModalAction,
  }
)(ProfileInfo);
